import * as React from "react"
import { useMutation, useQuery } from "@tanstack/react-query"
import { toast } from "sonner"
import { createFormStructureShare } from "@/api/create-form-structure-share"
import { Label } from "@/components/shadcn/ui/label"
import { Switch } from "@/components/shadcn/ui/switch"
import { Form as FormStructure, Field, FormType, Section } from "@/models/new/forms.types"
import getForms from "@/api/get-forms"
import useLoggedInUser from "@/hooks/use-logged-in-user"
import { fetchListItems } from "@/utils/api"
import { isSuccessStatus } from "@/utils/http"
import FormStructureViz from "./FormStructureViz"

interface ListItem {
  id: string
  value: string
  [key: string]: unknown
}

const overviewFormTypes: FormType[] = ["incident", "inspection", "issue"]

const findListFields = (fields: (Field | Section)[]): Set<string> => {
  const listCodes = new Set<string>()

  fields.forEach((field) => {
    if ("type" in field && field.type === "section") {
      const section = field as Section
      findListFields(section.fields).forEach((code) => listCodes.add(code))
      return
    }

    const typedField = field as Field
    if (typedField.type === "select") {
      const selectField = typedField as Extract<Field, { type: "select" }>
      if (selectField.options.source === "system" || selectField.options.source === "custom") {
        listCodes.add(selectField.options.list)
      }
    }

    if (typedField.type === "dynamicGroup") {
      const groupField = typedField as Extract<Field, { type: "dynamicGroup" }>
      findListFields(groupField.fields).forEach((code) => listCodes.add(code))
    }
  })

  return listCodes
}

const FormStructureOverview = () => {
  const loggedInUser = useLoggedInUser()
  const [includeOrganizationForms, setIncludeOrganizationForms] = React.useState(false)

  const canIncludeOrganizationForms = Boolean(loggedInUser?.organizationId)
  const organizationScopes = includeOrganizationForms && loggedInUser?.organizationId
    ? ["global", loggedInUser.organizationId]
    : ["global"]

  const createShareMutation = useMutation({
    mutationFn: () =>
      createFormStructureShare.mutate<{
        shareId: string
        path: string
      }>({
        body: {},
      }),
    onSuccess: async (response) => {
      if (!isSuccessStatus(response.status) || !response.data?.shareId) {
        toast.error("Failed to create share link")
        return
      }

      const sharePath = response.data.path || `/public/form-structure-overview/${response.data.shareId}`
      const shareUrl = `${window.location.origin}${sharePath}`

      try {
        await navigator.clipboard.writeText(shareUrl)
        toast.success("Share link copied to clipboard")
      } catch {
        toast.success("Share link created", { description: shareUrl })
      }
    },
    onError: () => {
      toast.error("Failed to create share link")
    },
  })

  const { data, isLoading, error } = useQuery({
    queryKey: ["formStructureOverview", organizationScopes],
    queryFn: async () => {
      const responses = await Promise.all(
        organizationScopes.flatMap((organizationId) =>
          overviewFormTypes.map((formType) =>
            getForms.call({
              queryParams: {
                form_type: formType,
                organizationId,
              },
            }),
          ),
        ),
      )

      if (responses.some(({ status }) => !isSuccessStatus(status))) {
        throw new Error("Failed to load forms")
      }

      const formData = Array.from(
        new Map(
          responses
            .flatMap(({ data }) => data as FormStructure[])
            .map((form) => [form.id, form] as const),
        ).values(),
      )
        .sort((left, right) => {
          if (left.type !== right.type) return left.type.localeCompare(right.type)
          if (left.organizationId !== right.organizationId) {
            return left.organizationId.localeCompare(right.organizationId)
          }
          return left.title.localeCompare(right.title)
        })

      const listCodes = new Set<string>()
      formData.forEach((form) => {
        findListFields(form.fields).forEach((code) => listCodes.add(code))
      })

      const listItemsResults = await Promise.all(
        Array.from(listCodes).map(async (listCode): Promise<[string, ListItem[]]> => {
          try {
            const items = await fetchListItems(listCode)
            return [listCode, items as ListItem[]]
          } catch {
            return [listCode, []]
          }
        }),
      )

      return {
        forms: formData,
        listItemsMap: Object.fromEntries(listItemsResults) as Record<string, ListItem[]>,
      }
    },
    enabled: !!loggedInUser,
  })

  if (isLoading) return <div className="p-6">Loading forms...</div>
  if (error) return <div className="p-6 text-red-600">Failed to load forms</div>

  return (
    <div>
      <div className="w-full max-w-6xl mx-auto px-6 pt-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex items-start gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3">
          <Switch
            id="include-organization-forms"
            checked={includeOrganizationForms}
            onCheckedChange={setIncludeOrganizationForms}
            disabled={!canIncludeOrganizationForms}
          />
          <div className="space-y-1">
            <Label htmlFor="include-organization-forms">Include organization forms</Label>
            <p className="text-sm text-slate-500">
              {canIncludeOrganizationForms
                ? "Show forms from your current organization alongside global templates."
                : "Only global templates are available until an organization scope is active."}
            </p>
          </div>
        </div>

        <div className="flex flex-col items-start gap-2 md:items-end">
          <button
            type="button"
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
            onClick={() => createShareMutation.mutate()}
            disabled={createShareMutation.isPending || includeOrganizationForms}
          >
            {createShareMutation.isPending ? "Creating Share Link..." : "Create Share Link"}
          </button>
          {includeOrganizationForms && (
            <p className="text-sm text-slate-500">
              Share links currently support the global-only form view.
            </p>
          )}
        </div>
      </div>

      <FormStructureViz forms={data?.forms ?? []} listItemsMap={data?.listItemsMap ?? {}} />
    </div>
  )
}

export default FormStructureOverview
